import { useState, useEffect, useContext } from 'react'
import { ClientContext } from '../../../contexts/clientProvider'
import { UserContext } from '../../../contexts/userProvider'
import { FornecedoresContext } from '../../../contexts/fornecedoresProvider'
import { dadosFornecedores, cadastroComponentsMap } from '../../General/Format/fornecedores'
import BaseInput from '../../General/FormsComp/Input/BaseInput';
import BaseSelect from '../../General/FormsComp/Select/BaseSelect';
import BaseButton from '../../General/FormsComp/Button/BaseButton';
import LoadingSpinner from '../../Loader/load';

const Cadastro = () => {
  const { estados } = useContext(ClientContext);
  const { createFornecedor } = useContext(FornecedoresContext);
  const { user, setAlert } = useContext(UserContext);
  const [municipios, setMunicipios] = useState([]);
  const [newFornecedor, setNewFornecedor] = useState({
    ativo: '1'
  });
  const [loading, setLoading] = useState(false);

  const fetchData = async (estadoNome) => {
    try {
      if (!estados || estados.length === 0) {
        console.warn('Estados não carregados ainda');
        return [];
      }
      
      // Encontrar o ID do estado pelo nome
      const estado = estados.find(e => e.nome === estadoNome);
      if (estado) {
        const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${estado.id}/municipios`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
      }
      return [];
    } catch (error) {
      console.error('Erro ao recuperar dados:', error);
      setAlert({
        visible: true,
        title: "Erro!",
        placeholder: "Erro ao carregar cidades. Tente novamente.",
        confirm: false,
        type: "warning"
      });
      return [];
    }
  };

  useEffect(() => {
    if (newFornecedor.estado) {
      fetchData(newFornecedor.estado).then(setMunicipios);
    } else {
      setMunicipios([]);
      // Limpar município se estado for removido
      if (newFornecedor.municipio) {
        setNewFornecedor(prev => ({ ...prev, municipio: '' }));
      }
    }
  }, [newFornecedor.estado, estados]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewFornecedor((prevData) => ({ ...prevData, [name]: value }));
  };

  const validateCNPJ = (cnpj) => {
    // Remove caracteres não numéricos
    cnpj = cnpj.replace(/\D/g, '');
    
    // Verifica se tem 14 dígitos
    if (cnpj.length !== 14) return false;
    
    // Verifica sequências inválidas
    if (/^(\d)\1+$/.test(cnpj)) return false;
    
    // Validação dos dígitos verificadores
    let soma = 0;
    let multiplicador = 2;
    
    for (let i = 11; i >= 0; i--) {
      soma += parseInt(cnpj.charAt(i)) * multiplicador;
      multiplicador = multiplicador === 9 ? 2 : multiplicador + 1;
    }
    
    let digito1 = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (parseInt(cnpj.charAt(12)) !== digito1) return false;
    
    soma = 0;
    multiplicador = 2;
    
    for (let i = 12; i >= 0; i--) {
      soma += parseInt(cnpj.charAt(i)) * multiplicador;
      multiplicador = multiplicador === 9 ? 2 : multiplicador + 1;
    }
    
    let digito2 = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    return parseInt(cnpj.charAt(13)) === digito2;
  };

  const handleSaveClick = async () => {
    // Validação de campos obrigatórios
    if (!newFornecedor.nome || newFornecedor.nome.trim() === '') {
      setAlert({
        visible: true,
        title: "Atenção!",
        placeholder: "O nome do fornecedor é obrigatório",
        confirm: false,
        type: "warning"
      });
      return;
    }

    if (!newFornecedor.cpf_cnpj || newFornecedor.cpf_cnpj.trim() === '') {
      setAlert({
        visible: true,
        title: "Atenção!",
        placeholder: "O CNPJ é obrigatório",
        confirm: false,
        type: "warning"
      });
      return;
    }

    // Validação do CNPJ
    if (!validateCNPJ(newFornecedor.cpf_cnpj)) {
      setAlert({
        visible: true,
        title: "Atenção!",
        placeholder: "CNPJ inválido. Verifique os dados informados.",
        confirm: false,
        type: "warning"
      });
      return;
    }

    if (!newFornecedor.estado || newFornecedor.estado.trim() === '') {
      setAlert({
        visible: true,
        title: "Atenção!",
        placeholder: "O estado é obrigatório",
        confirm: false,
        type: "warning"
      });
      return;
    }

    if (!newFornecedor.municipio || newFornecedor.municipio.trim() === '') {
      setAlert({
        visible: true,
        title: "Atenção!",
        placeholder: "A cidade é obrigatória",
        confirm: false,
        type: "warning"
      });
      return;
    }

    setLoading(true);
    try {
      let fornecedor = { ...newFornecedor, id_usuario: user.id, usuario: user.nome }
      fornecedor.created_at = fornecedor.updated_at = new Date().toISOString().slice(0, 19).replace('T', ' ');
      
      const success = await createFornecedor(fornecedor);
      if (success !== false) {
        // Limpar formulário após sucesso
        setNewFornecedor({ ativo: '1' });
        setMunicipios([]);
      }
    } catch (error) {
      console.error('Erro ao salvar fornecedor:', error);
      setAlert({
        visible: true,
        title: "Erro!",
        placeholder: "Erro interno ao salvar fornecedor. Tente novamente.",
        confirm: false,
        type: "negative"
      });
    } finally {
      setLoading(false);
    }
  };

  const dadosGeral = dadosFornecedores(newFornecedor);
  const componentMap = cadastroComponentsMap(handleInputChange);

  // Verificar se dados essenciais estão carregados
  if (!estados) {
    return (
      <div className="w-full max-w-6xl mx-auto p-6">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando dados necessários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      {loading && <LoadingSpinner />}
      
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Cadastro de Fornecedor
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Preencha as informações do fornecedor. Campos marcados com * são obrigatórios.
        </p>
      </div>

      {/* Informações Básicas */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-vox-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Informações Básicas
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Situação */}
          <div>
            {componentMap['Cadastro'](dadosGeral[0])}
          </div>

          {/* CNPJ */}
          <div>
            {componentMap['Cadastro'](dadosGeral[1])}
          </div>

          {/* Nome do Fornecedor */}
          <div className="lg:col-span-1">
            {componentMap['Cadastro'](dadosGeral[2])}
          </div>

          {/* Telefone */}
          <div>
            {componentMap['Cadastro'](dadosGeral[3])}
          </div>
        </div>
      </div>

      {/* Informações de Endereço */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-vox-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Endereço
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Endereço */}
          <div className="lg:col-span-2">
            {componentMap['Cadastro'](dadosGeral[4])}
          </div>

          {/* Número */}
          <div>
            {componentMap['Cadastro'](dadosGeral[5])}
          </div>

          {/* Estado */}
          <div>
            <BaseSelect
              label="Estado *"
              name="estado"
              value={newFornecedor.estado || ''}
              onChange={handleInputChange}
              options={estados || []}
              placeholder="Selecione o estado"
              valueKey="nome"
              labelKey="nome"
              required
            />
          </div>
          
          {/* Cidade */}
          <div className="lg:col-span-3">
            <BaseSelect
              label="Cidade *"
              name="municipio"
              value={newFornecedor.municipio || ''}
              onChange={handleInputChange}
              options={municipios || []}
              placeholder={!newFornecedor.estado ? "Selecione um estado primeiro" : "Selecione a cidade"}
              valueKey="nome"
              labelKey="nome"
              required
              disabled={!newFornecedor.estado}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end">
        <BaseButton 
          onClick={handleSaveClick} 
          variant="primary" 
          size="lg"
          disabled={loading}
          className="min-w-[150px]"
        >
          {loading ? 'Salvando...' : 'Salvar Fornecedor'}
        </BaseButton>
      </div>
    </div>
  )
}

export default Cadastro
